import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { IMG, TOP_BAR } from '../mock/mock';

function Section({ num, title, children }) {
  return (
    <section className="mb-12">
      <div className="flex items-baseline gap-4 mb-5">
        <span className="font-serif-display text-[#2BA8C0] text-3xl md:text-4xl leading-none shrink-0">{num}</span>
        <h2 className="font-serif-display text-[#1a1a1a] text-2xl md:text-3xl leading-tight">{title}</h2>
      </div>
      <div className="text-[#3a3a3a] text-[15px] leading-relaxed space-y-3 pl-0 md:pl-12">{children}</div>
    </section>
  );
}

function SubSection({ letter, title, children }) {
  return (
    <div className="mt-7">
      <h3 className="text-[#1a1a1a] font-semibold text-[16px] mb-2 flex items-baseline gap-2">
        <span className="text-[#2BA8C0] font-serif-display text-lg">{letter})</span>
        {title}
      </h3>
      <div className="text-[#3a3a3a] text-[14.5px] leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#f5f0e8]">
      {/* Top hero */}
      <div className="relative bg-gradient-to-b from-[#0e2030] via-[#0d2336] to-[#0a1d2e] text-white pt-12 pb-32 overflow-hidden">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#2BA8C0] text-[14px] mb-10 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-px bg-[#2BA8C0]" />
            <span className="text-[12px] uppercase tracking-[0.3em] text-[#2BA8C0] font-medium">Jogi tájékoztató</span>
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] mb-5">
            Adatvédelmi tájékoztató<br /><span className="italic text-[#2BA8C0]">és nyilatkozat</span>
          </h1>
          <p className="text-white/65 text-[15px]">Hatályos: 2026. 04. 16. – től visszavonásig, vagy módosításig</p>
        </div>
      </div>

      {/* Content card overlapping hero */}
      <div className="max-w-4xl mx-auto px-6 -mt-20 relative z-10 pb-24">
        <article className="bg-white rounded-3xl shadow-xl border border-[#e8e4dc] p-8 md:p-14">

          {/* Intro */}
          <div className="mb-12 pb-10 border-b border-[#e8e4dc]">
            <p className="text-[#3a3a3a] text-[15px] leading-relaxed mb-5">
              Ez az Adatvédelmi tájékoztató és nyilatkozat (a továbbiakban: <strong>Tájékoztató</strong>) az
              <strong> Eco-Lak Építő Kft.</strong> (továbbiakban: <strong>Adatkezelő</strong>) tevékenységére vonatkozóan
              tartalmazza az adatvédelemmel kapcsolatos információkat.
            </p>
            <p className="text-[#3a3a3a] text-[15px] leading-relaxed">
              Szolgáltatásunk igénybevétele során Ön megadja személyes adatait. Ezeket az adatokat a legnagyobb
              körültekintéssel, a jogszabályok előírásainak megfelelően kezeljük, és minden esetben igyekszünk
              az Ön adatkezeléssel kapcsolatos igényeinek és elvárásainak megfelelni. Az adatok kezelése során
              mindig különös gondossággal járunk el, és az adatokat védjük az illetéktelen hozzáféréstől.
            </p>
          </div>

          <Section num="01" title="Adatkezelő adatai">
            <div className="bg-[#f5f0e8] rounded-2xl p-6 grid sm:grid-cols-2 gap-4 text-[14px]">
              <div>
                <p className="text-[12px] uppercase tracking-wider text-[#7a7a7a] mb-1">Cégnév</p>
                <p className="text-[#1a1a1a] font-medium">Eco-Lak Építő Kft.</p>
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wider text-[#7a7a7a] mb-1">Székhely</p>
                <p className="text-[#1a1a1a] font-medium">8000 Székesfehérvár,<br />Budai út 14. Fsz. 1. ajtó</p>
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wider text-[#7a7a7a] mb-1">Adószám</p>
                <p className="text-[#1a1a1a] font-medium">32534627-2-07</p>
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wider text-[#7a7a7a] mb-1">Cégjegyzékszám</p>
                <p className="text-[#1a1a1a] font-medium">07 09 035653</p>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#2BA8C0]" />
                <a href={`tel:${TOP_BAR.phone}`} className="text-[#1a1a1a] font-medium hover:text-[#2BA8C0]">{TOP_BAR.phone}</a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#2BA8C0]" />
                <a href={`mailto:${TOP_BAR.email}`} className="text-[#1a1a1a] font-medium hover:text-[#2BA8C0]">{TOP_BAR.email}</a>
              </div>
            </div>
          </Section>

          <Section num="02" title="Adatvédelmi nyilatkozat">
            <p>Az adatkezelési tevékenységünket meghatározó jogszabályok elsősorban:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#2BA8C0]">
              <li>Az Európai Parlament és a Tanács (EU) 2016/679 rendelete (GDPR)</li>
              <li>Az információs önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII. törvény (Infotv.)</li>
              <li>Magyarország Alaptörvénye</li>
              <li>A Polgári törvénykönyvről szóló 2013. évi V. törvény (Ptk.)</li>
              <li>2003. évi C. törvény az elektronikus hírközlésről (Eht.)</li>
              <li>Az adózás rendjéről szóló 2017. évi CL. törvény (Art.)</li>
              <li>A számvitelről szóló 2000. évi C. törvény (Sztv.)</li>
              <li>A fogyasztóvédelemről szóló 1997. évi CLV. törvény (Fogyv tv.)</li>
              <li>2008. évi XLVIII. törvény (Grtv.)</li>
            </ul>
            <p className="pt-2">
              A Tájékoztató célja, hogy ismertesse a szolgáltatásunkat igénybe vevő személyek adatátadással,
              adatkezeléssel, adatvédelemmel kapcsolatos jogait, kötelezettségeit, az általunk kezelt adatok
              körét, az adatok kezelésének elvét és módszereit, célját, jogalapját és időtartamát.
            </p>
          </Section>

          <Section num="03" title="Fogalmak">
            <dl className="space-y-4">
              {[
                ['GDPR', 'Az Európai Parlament és a Tanács (EU) 2016/679 rendelete (2016. április 27.) – általános adatvédelmi rendelet.'],
                ['Személyes adat', 'Az érintettre vonatkozó bármely információ, például azonosító, név, helymeghatározó adat, online azonosító, vagy a természetes személy fizikai, gazdasági, kulturális azonosságára vonatkozó adat.'],
                ['Különleges adatok', 'Faji vagy etnikai származásra, politikai véleményre, vallási vagy világnézeti meggyőződésre, szakszervezeti tagságra, genetikai és biometrikus adatokra, egészségügyi adatokra, illetve szexuális életre vonatkozó személyes adatok.'],
                ['Adatkezelés', 'A személyes adatokon végzett bármely művelet vagy műveletek összessége (gyűjtés, rögzítés, tárolás, módosítás, lekérdezés, továbbítás, törlés stb.).'],
                ['Adatkezelő', 'Az a természetes vagy jogi személy, aki a személyes adatok kezelésének célját és eszközeit meghatározza.'],
                ['Adatfeldolgozó', 'Aki az adatkezelő nevében személyes adatokat kezel.'],
                ['Érintett', 'Az azonosított vagy azonosítható természetes személy, akire a személyes adat vonatkozik.'],
                ['Adattovábbítás', 'A személyes adat meghatározott harmadik személy számára történő hozzáférhetővé tétele.'],
                ['Adatvédelmi incidens', 'A biztonság olyan sérülése, amely a továbbított, tárolt vagy más módon kezelt személyes adatok véletlen vagy jogellenes megsemmisítését, elvesztését, megváltoztatását, jogosulatlan közlését eredményezi.'],
                ['NAIH', 'Nemzeti Adatvédelmi és Információszabadság Hatóság – Magyarország felügyeleti hatósága a GDPR szerint.'],
              ].map(([term, def]) => (
                <div key={term} className="bg-[#faf7f1] rounded-xl p-4 border border-[#ece7df]">
                  <dt className="font-serif-display text-[#2BA8C0] text-lg mb-1">{term}</dt>
                  <dd className="text-[#3a3a3a] text-[14.5px]">{def}</dd>
                </div>
              ))}
            </dl>
          </Section>

          <Section num="04" title="Az adatkezelés alapvető elvei">
            <p>A személyes adatok:</p>
            <ul className="space-y-2.5">
              {[
                ['Jogszerűség, tisztességes eljárás és átláthatóság', 'Az adatkezelést jogszerűen, tisztességesen, átlátható módon végezzük.'],
                ['Célhoz kötöttség', 'A gyűjtés csak meghatározott, egyértelmű és jogszerű célból történik.'],
                ['Adattakarékosság', 'Az adatkezelés céljaihoz szükséges minimális mértékre korlátozódik.'],
                ['Pontosság', 'Az adatok pontosak és naprakészek; a pontatlan adatokat haladéktalanul töröljük vagy helyesbítjük.'],
                ['Korlátozott tárolhatóság', 'Az adatokat csak a célok eléréséhez szükséges ideig tároljuk.'],
                ['Integritás és bizalmas jelleg', 'Megfelelő technikai és szervezési intézkedésekkel biztosítjuk az adatok biztonságát.'],
              ].map(([title, desc]) => (
                <li key={title} className="flex gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2BA8C0] mt-2.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-[#1a1a1a]">{title}.</span>{' '}
                    <span>{desc}</span>
                  </div>
                </li>
              ))}
            </ul>
            <p className="pt-2 italic">
              Adatkezelőként felelősek vagyunk a fentieknek való megfelelésért, szükség esetén a megfelelést
              igazoljuk („elszámoltathatóság”).
            </p>
          </Section>

          <Section num="05" title="Kezelt adatok">
            <h3 className="text-[#1a1a1a] font-semibold mb-2">5.1 Szolgáltatás megrendelése és időpontfoglalás (kérdőív kitöltéssel)</h3>
            <p>
              <strong>Érintettek:</strong> azok a természetes/jogi személyek, akik a szolgáltatást
              megrendelik és időpontot foglalnak. Az adatokat a megrendelés teljesítéséhez és kapcsolattartáshoz használjuk.
            </p>
            <div className="overflow-hidden rounded-2xl border border-[#e8e4dc] mt-4">
              <table className="w-full text-[14px]">
                <thead className="bg-[#f5f0e8]">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-[#1a1a1a]">Adatfajta</th>
                    <th className="text-left px-5 py-3 font-semibold text-[#1a1a1a]">Jogalap</th>
                    <th className="text-left px-5 py-3 font-semibold text-[#1a1a1a]">Őrzési idő</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e8e4dc] bg-white">
                  {[
                    ['Név', 'GDPR 6. cikk a) pont (hozzájárulás)', 'Hozzájárulás visszavonásáig'],
                    ['E-mail cím', 'GDPR 6. cikk a) pont (hozzájárulás)', 'Hozzájárulás visszavonásáig'],
                    ['Telefonszám', 'GDPR 6. cikk a) pont (hozzájárulás)', 'Hozzájárulás visszavonásáig'],
                    ['Lakcím', 'GDPR 6. cikk a) pont (hozzájárulás)', 'Hozzájárulás visszavonásáig'],
                    ['Időpont', 'GDPR 6. cikk a) pont (hozzájárulás)', 'Hozzájárulás visszavonásáig'],
                  ].map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-5 py-3 text-[#3a3a3a]">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section num="06" title="Az adatok biztonsága">
            <p>
              Az adatokat megfelelő intézkedésekkel védjük a jogosulatlan hozzáférés, megváltoztatás,
              továbbítás, nyilvánosságra hozatal, törlés vagy megsemmisítés ellen. A személyes adatokhoz
              csak azok a munkatársaink férnek hozzá, akiknek feladataik ellátásához erre szükségük van.
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#2BA8C0]">
              <li>Felmérjük és figyelembe vesszük a lehetséges kockázatokat, törekedve azok folyamatos csökkentésére.</li>
              <li>Figyelemmel kísérjük a felmerülő fenyegetéseket és sérülékenységeket (vírusok, betörések stb.).</li>
              <li>Védjük az informatikai eszközöket és a papíron kezelt információkat fizikai és környezeti hatásoktól.</li>
              <li>Megfigyeljük az informatikai rendszerünket az események felderítése érdekében.</li>
              <li>Az üzemeltetésben résztvevő szolgáltatókat megbízhatóság alapján választjuk.</li>
            </ul>
          </Section>

          <Section num="07" title="Az adatok továbbítása, átadása">
            <p>
              A szolgáltatásunkat igénybe vevő természetes személyek személyes adatait kizárólag az alábbi
              partnereinknek, adatfeldolgozóinknak továbbítjuk, valamint a hatóságoknak megkeresés esetén.
              Minden partnerrel az adatkezelés részleteire kiterjedő írásbeli megállapodást kötöttünk.
            </p>
            <div className="bg-[#faf7f1] rounded-2xl p-5 border border-[#ece7df]">
              <p className="text-[12px] uppercase tracking-wider text-[#7a7a7a] mb-2">Adatfeldolgozó partnerünk</p>
              <p className="font-semibold text-[#1a1a1a]">HighLevel Inc. (tárhelyszolgáltató)</p>
              <p className="text-[14px] text-[#5a5a5a]">400 North Saint Paul Street, Suite 920, Dallas, TX 75201</p>
            </div>
          </Section>

          <Section num="08" title="Az érintettek jogai">
            <SubSection letter="a" title="Előzetes tájékoztatáshoz való jog">
              <p>Az érintett jogosult arra, hogy az adatkezelés megkezdése előtt átláthatóan, érthetően, világosan és könnyen hozzáférhetően írásban tájékoztatást kapjon az Adatkezelőtől.</p>
            </SubSection>
            <SubSection letter="b" title="A hozzáférés joga">
              <p>Visszajelzést kérhet arra vonatkozóan, hogy személyes adatainak kezelése folyamatban van-e, és hozzáférhet az adatokhoz, az adatkezelés céljaihoz, kategóriáihoz, címzettjeihez, tárolási idejéhez, valamint a jogairól szóló információkhoz.</p>
            </SubSection>
            <SubSection letter="c" title="A helyesbítéshez való jog">
              <p>Kérheti a pontatlan személyes adatok helyesbítését, vagy a hiányos adatok kiegészítését.</p>
            </SubSection>
            <SubSection letter="d" title="A törléshez való jog („elfeledtetéshez való jog”)">
              <p>Bizonyos feltételek esetén kérheti személyes adatainak törlését (pl. ha visszavonja hozzájárulását, ha az adatkezelés célja megszűnt, ha az adatkezelés jogellenes).</p>
            </SubSection>
            <SubSection letter="e" title="Az adatkezelés korlátozásához való jog">
              <p>Kérheti az adatkezelés korlátozását, ha vitatja az adatok pontosságát, az adatkezelés jogellenes, vagy ha tiltakozott az adatkezelés ellen.</p>
            </SubSection>
            <SubSection letter="f" title="Adathordozhatósághoz való jog">
              <p>Kérheti, hogy a rá vonatkozó személyes adatokat tagolt, géppel olvasható formátumban megkapja, és továbbítsa egy másik adatkezelőnek.</p>
            </SubSection>
            <SubSection letter="g" title="A tiltakozáshoz való jog">
              <p>Tiltakozhat a személyes adatainak kezelése ellen, különösen közvetlen üzletszerzés céljából történő kezelés esetén.</p>
            </SubSection>
            <SubSection letter="h" title="Az adatvédelmi incidens értesítéshez való jog">
              <p>Joga van tájékoztatást kapni az őt érintő adatvédelmi incidensről, ha az magas kockázattal jár jogaira és szabadságaira.</p>
            </SubSection>
            <SubSection letter="i" title="Panasztétel a felügyeleti hatóságnál">
              <div className="bg-[#f5f0e8] rounded-xl p-4 border border-[#ece7df]">
                <p className="font-semibold text-[#1a1a1a] mb-2">Nemzeti Adatvédelmi és Információszabadság Hatóság (NAIH)</p>
                <ul className="space-y-1 text-[14px]">
                  <li><strong>Postacím:</strong> 1363 Budapest, Pf. 9.</li>
                  <li><strong>Székhely:</strong> 1055 Budapest, Falk Miksa utca 9-11.</li>
                  <li><strong>Telefon:</strong> 06-1-391-1400</li>
                  <li><strong>E-mail:</strong> ugyfelszolgalat@naih.hu</li>
                  <li><strong>Honlap:</strong> www.naih.hu</li>
                </ul>
              </div>
            </SubSection>
          </Section>

          {/* Contact CTA */}
          <div className="mt-14 pt-10 border-t border-[#e8e4dc]">
            <h2 className="font-serif-display text-2xl md:text-3xl text-[#1a1a1a] mb-3">Jogainak gyakorlása</h2>
            <p className="text-[#3a3a3a] text-[15px] leading-relaxed mb-6">
              A jogokat az alábbi elérhetőségeink használatával írásban, illetve előzetes egyeztetés alapján
              személyesen gyakorolhatja. Minden megkeresésre legfeljebb <strong>15 munkanapon</strong> belül válaszolunk.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <a href={`mailto:${TOP_BAR.email}`} className="group flex items-center gap-3 px-5 py-4 bg-[#f5f0e8] hover:bg-[#2BA8C0] hover:text-white rounded-2xl transition-colors border border-[#ece7df]">
                <Mail className="w-5 h-5 text-[#2BA8C0] group-hover:text-white transition-colors" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#7a7a7a] group-hover:text-white/80 transition-colors">E-mail</p>
                  <p className="text-[13px] font-medium">{TOP_BAR.email}</p>
                </div>
              </a>
              <a href={`tel:${TOP_BAR.phone}`} className="group flex items-center gap-3 px-5 py-4 bg-[#f5f0e8] hover:bg-[#2BA8C0] hover:text-white rounded-2xl transition-colors border border-[#ece7df]">
                <Phone className="w-5 h-5 text-[#2BA8C0] group-hover:text-white transition-colors" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#7a7a7a] group-hover:text-white/80 transition-colors">Telefon</p>
                  <p className="text-[13px] font-medium">{TOP_BAR.phone}</p>
                </div>
              </a>
              <div className="flex items-center gap-3 px-5 py-4 bg-[#f5f0e8] rounded-2xl border border-[#ece7df]">
                <MapPin className="w-5 h-5 text-[#2BA8C0]" />
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#7a7a7a]">Postacím</p>
                  <p className="text-[13px] font-medium text-[#1a1a1a]">8000 Székesfehérvár,<br />Budai út 14.</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <div className="text-center mt-10">
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0a1d2e] text-white text-[14px] font-medium hover:bg-[#2BA8C0] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Vissza a főoldalra
          </Link>
        </div>
      </div>
    </div>
  );
}
